import { transformSummaries } from 'hooks/tracked_meetings/api'
import { camelize } from 'services/string_utils'
import { CALENDLY_FOREIGN_EVENT_PROVIDER } from 'shared/utils/foreign_event_provider'

const PARTICIPANT_ROLE_TO_ATTENDEE_KIND: Record<string, string> = {
  HOST: 'HOST',
  COHOST: 'COHOST',
  ATTENDEE: 'INVITEE',
}

const SUMMARY_TARGET_NAMES = {
  SUMMARY: 'Summary',
  ACTION_ITEMS: 'Action Items',
  DISCUSSION: 'Discussion',
} as const

type SummaryTargetName = typeof SUMMARY_TARGET_NAMES[keyof typeof SUMMARY_TARGET_NAMES]

export type MeetingRecapParticipant = {
  identifier?: string | null
  email?: string | null
  name?: string | null
  role?: string | null
  status?: string | null
  noShow?: boolean | null
  rescheduled?: boolean | null
}

export type MeetingForRecap = {
  uuid?: string | null
  ownerUuid?: string | null
  kind?: string | null
  title?: string | null
  startTime?: string | null
  endTime?: string | null
  location?: string | null
  participants?: MeetingRecapParticipant[] | null
}

export type TrackedMeetingResource = Record<string, unknown> & {
  uuid?: string
  status?: string
  error?: string | null
  thumbnail?: string | null
  video_url?: string | null
  recap_permission?: string | null
  user_uuid?: string | null
  name?: string | null
  foreign_event_id?: string | null
  foreign_event_provider?: string | null
  location?: string | null
  start_time?: string | null
  end_time?: string | null
}

export type MeetingRecapSummaryEntry = {
  name?: string | null
  kind?: string | null
  value?: unknown
  [key: string]: unknown
}

export type TrackedMeetingSummaryResource = {
  transcript?: unknown
  summaries?: MeetingRecapSummaryEntry[] | null
  [key: string]: unknown
}

export type MeetingRecapPageGraphqlResponse = {
  meetingForRecap?: MeetingForRecap | null
  get_tracked_meeting?: {
    resource?: TrackedMeetingResource | null
  } | null
  get_tracked_meeting_summary?: {
    resource?: TrackedMeetingSummaryResource | null
  } | null
}

export type MeetingRecapAttendee = {
  kind: string
  name?: string | null
  email?: string | null
  uuid?: string | null
  cancelled: boolean
  noShow: boolean
  rescheduled: boolean
}

export type MeetingRecapMeetingNode = {
  uuid?: string
  name?: string
  kind?: string | null
  owner_user_uuid?: string | null
  start_time?: string | null
  end_time?: string | null
  location?: string | null
  attendees: MeetingRecapAttendee[]
}

export type MeetingRecapPageData = Record<string, unknown> & {
  uuid?: string
  name?: string
  userUuid?: string | null
  foreignEventId?: string
  foreignEventProvider?: string | null
  location?: string | null
  startTime?: string | null
  endTime?: string | null
  summaries?: Record<string, unknown>
}

export type MeetingRecapPageError = {
  type: string
}

export type MeetingRecapPageMappedResult = {
  data: MeetingRecapPageData | null
  meetingNode: MeetingRecapMeetingNode | null
  error: MeetingRecapPageError | null
}

const normalizeSummaryName = (name: unknown): unknown => {
  if (!name || typeof name !== 'string') return name
  if (name === 'Action_Items') return SUMMARY_TARGET_NAMES.ACTION_ITEMS
  return name.replace(/_/g, ' ')
}

export const mapParticipantsToAttendees = (
  participants: MeetingRecapParticipant[] | null | undefined,
): MeetingRecapAttendee[] => {
  if (!Array.isArray(participants)) return []
  return participants.map((participant) => ({
    kind: PARTICIPANT_ROLE_TO_ATTENDEE_KIND[participant?.role ?? ''] ?? 'INVITEE',
    name: participant?.name,
    email: participant?.email,
    uuid: participant?.identifier,
    cancelled: participant?.status === 'DECLINED',
    noShow: Boolean(participant?.noShow),
    rescheduled: Boolean(participant?.rescheduled),
  }))
}

export const mapKindToForeignEventProvider = (kind: string | null | undefined): string | null => {
  if (!kind || typeof kind !== 'string') return null
  const normalized = kind.trim().toUpperCase()
  if (normalized === 'CALENDLY' || normalized === 'CALENDLY_DESKTOP') {
    return CALENDLY_FOREIGN_EVENT_PROVIDER
  }
  return normalized
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const unwrapSummaryValue = (value: unknown): unknown => {
  if (value == null) return value
  if (typeof value === 'string') return value
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (typeof record.String === 'string') return record.String
    if (typeof record.string === 'string') return record.string
  }
  return value
}

const normalizeSummaryEntries = (summaries: unknown): MeetingRecapSummaryEntry[] => {
  if (!Array.isArray(summaries)) return []
  return summaries.map((item: MeetingRecapSummaryEntry) => ({
    ...item,
    name: normalizeSummaryName(item?.name) as string | null | undefined,
    value: unwrapSummaryValue(item?.value),
  }))
}

const findSummaryValue = (
  summaries: MeetingRecapSummaryEntry[] | undefined,
  targetName: SummaryTargetName,
): unknown => (
  summaries?.find((item) => item?.name === targetName)?.value
)

const buildMeetingNode = (
  meetingForRecap: MeetingForRecap | null,
  trackedMeeting: MeetingRecapPageData,
): MeetingRecapMeetingNode | null => {
  if (!meetingForRecap && !trackedMeeting) return null

  if (!meetingForRecap) {
    return {
      uuid: (trackedMeeting.foreignEventId as string | undefined) ?? trackedMeeting.uuid,
      name: trackedMeeting.name,
      kind: trackedMeeting.foreignEventProvider,
      owner_user_uuid: trackedMeeting.userUuid,
      start_time: trackedMeeting.startTime,
      end_time: trackedMeeting.endTime,
      location: trackedMeeting.location,
      attendees: [],
    }
  }

  return {
    uuid: meetingForRecap.uuid ?? undefined,
    name: meetingForRecap.title ?? undefined,
    kind: meetingForRecap.kind,
    owner_user_uuid: meetingForRecap.ownerUuid,
    start_time: meetingForRecap.startTime,
    end_time: meetingForRecap.endTime,
    location: meetingForRecap.location,
    attendees: mapParticipantsToAttendees(meetingForRecap.participants),
  }
}

/**
 * Maps the supergraph MeetingRecapPage response into the shape TrackedMeetingsDetails expects.
 */
export const mapMeetingRecapPageResponse = (
  response: MeetingRecapPageGraphqlResponse | null | undefined,
): MeetingRecapPageMappedResult => {
  const trackedResource = response?.get_tracked_meeting?.resource
  if (!trackedResource) {
    return {
      data: null,
      meetingNode: null,
      error: { type: 'not_found' },
    }
  }

  const meetingForRecap = response?.meetingForRecap ?? null
  const summaryResource = response?.get_tracked_meeting_summary?.resource ?? {}

  const camelizedTracked = camelize(trackedResource) as MeetingRecapPageData
  const camelizedSummary = camelize(summaryResource) as Record<string, unknown>
  const normalizedSummaries = normalizeSummaryEntries(camelizedSummary.summaries)

  const transformedSummaries = transformSummaries({
    ...camelizedSummary,
    summaries: normalizedSummaries,
  }) as Record<string, unknown> & {
    summaries?: MeetingRecapSummaryEntry[]
    summary?: unknown
    actionItems?: unknown
    topics?: unknown
  }

  const summaryList = transformedSummaries.summaries ?? []
  const summaries = {
    ...transformedSummaries,
    summary: transformedSummaries.summary ?? findSummaryValue(summaryList, SUMMARY_TARGET_NAMES.SUMMARY),
    actionItems: transformedSummaries.actionItems
      ?? findSummaryValue(summaryList, SUMMARY_TARGET_NAMES.ACTION_ITEMS),
    topics: transformedSummaries.topics ?? findSummaryValue(summaryList, SUMMARY_TARGET_NAMES.DISCUSSION),
  }

  const foreignEventProvider = mapKindToForeignEventProvider(meetingForRecap?.kind)
    ?? (camelizedTracked.foreignEventProvider as string | null | undefined)
    ?? null

  const data: MeetingRecapPageData = {
    ...camelizedTracked,
    name: meetingForRecap?.title ?? camelizedTracked.name,
    userUuid: meetingForRecap?.ownerUuid ?? camelizedTracked.userUuid,
    // Prefer meeting uuid from meetingForRecap; otherwise the tracked
    // meeting's foreign_event_id. Never substitute the tracked-meeting uuid.
    foreignEventId: meetingForRecap?.uuid
      ?? (camelizedTracked.foreignEventId as string | undefined)
      ?? undefined,
    foreignEventProvider,
    location: meetingForRecap?.location ?? camelizedTracked.location,
    startTime: meetingForRecap?.startTime ?? camelizedTracked.startTime,
    endTime: meetingForRecap?.endTime ?? camelizedTracked.endTime,
    summaries,
  }

  return {
    data,
    meetingNode: buildMeetingNode(meetingForRecap, data),
    error: null,
  }
}
